/**
 * Exit Intent VIDEO Popup — Empty-cart variant
 * --------------------------------------------
 * Renders on every page (via layout/theme.liquid). Fires when the visitor
 * shows intent to leave AND the cart is currently EMPTY (item_count === 0).
 *
 * Desktop: mouseleave at top of viewport
 * Mobile:  back button + app switch + scroll-up burst + idle timeout
 *
 * Differences from the regular exit-intent popup:
 *   - Fires only when cart is EMPTY (regular one fires when cart has items)
 *   - Content is just a video (autoplay, muted, with an unmute toggle)
 *
 * Shown at most once per 24 hours, on its own clock — independent of the
 * regular exit-intent popup's clock.
 *
 * Lifecycle:
 *   1. Wait ARM_DELAY_MS, then arm the exit triggers unconditionally
 *   2. On trigger → confirm the cart is empty → show modal, autoplay muted
 *   3. On close → reset video, keep triggers armed so it can fire again
 *
 * The cart gate is evaluated at TRIGGER time, never at arm time. Arming used to
 * be gated on the cart count as well, which produced the exact bug this file
 * now guards against: a visitor landed with an empty cart, added an item, and
 * on exit this video opened off the stale count of 0 from page load and then
 * retracted itself when the background re-check disagreed — while the regular
 * popup, which should have fired, had never armed its triggers.
 *
 * Only one popup may be on screen at a time, and the cart count is shared;
 * window.ThemePopups (assets/popup-manager.js) owns both, plus the scroll lock
 * and the background blur.
 */

(function () {
  'use strict';

  const POPUP_ID = 'exit-intent-video';

  const popup = document.getElementById('ExitIntentVideoPopup');
  if (!popup) return;

  // The manager owns the mutex, the scroll lock and the cart count. Without it
  // there is no safe way to show this popup, so bail rather than half-work.
  const Popups = window.ThemePopups;
  if (!Popups) return;

  // How long after page load the exit triggers start listening. Hardcoded on
  // purpose: this used to be a theme setting, and a value saved in the theme
  // editor silently overrode the intended timing.
  const ARM_DELAY_MS = 2000;

  // `parseInt(x, 10) || fallback` silently turns a configured 0 into the
  // fallback, because 0 is falsy. Treat any non-negative number as intentional.
  function readSeconds(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  const config = {
    mobileIdleSeconds: readSeconds(popup.dataset.mobileIdleSeconds, 30),
  };

  const video = popup.querySelector('#ExitIntentVideoPlayer');
  const soundBtn = popup.querySelector('[data-exit-intent-video-sound]');

  // =========================================
  // ONCE PER DAY, PER VISITOR
  // Shown at most once every 24 hours. The timestamp lives in localStorage so
  // it survives closing the tab; sessionStorage and then an in-memory value
  // are used as fallbacks when storage is blocked (private mode, strict
  // cookie policies), which at worst degrades to once per session / per load.
  //
  // The window is deliberately hardcoded — there is no theme setting for it.
  // This key is separate from the regular exit-intent popup's, so each popup
  // gets its own once-a-day showing.
  // =========================================
  const STORAGE_KEY = 'exitIntentVideoLastShownAt';
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  let memoryLastShown = null;

  function readLastShown() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) return parseInt(stored, 10);
    } catch (e) { /* localStorage blocked — try sessionStorage */ }

    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (stored) return parseInt(stored, 10);
    } catch (e) { /* sessionStorage blocked too */ }

    return memoryLastShown;
  }

  function writeLastShown(timestamp) {
    memoryLastShown = timestamp;

    try {
      window.localStorage.setItem(STORAGE_KEY, String(timestamp));
      return;
    } catch (e) { /* localStorage blocked — try sessionStorage */ }

    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(timestamp));
    } catch (e) { /* storage fully blocked — memory only */ }
  }

  function shownWithinDay() {
    const last = readLastShown();
    if (!last) return false; // never shown, or an unparseable value

    const age = Date.now() - last;
    if (age < 0) return false; // clock moved backwards → treat as expired

    return age < ONE_DAY_MS;
  }

  function markShown() {
    writeLastShown(Date.now());
  }

  if (shownWithinDay()) return;

  // =========================================
  // CART GATE — this popup applies only to an EMPTY cart
  //
  // The count comes from the shared /cart.js cache, never from Liquid, which
  // goes stale the moment anything is added over AJAX. A null count means the
  // check failed, not that the cart is empty, so it must never pass the gate.
  // =========================================
  function gatePasses(count) {
    return count === 0;
  }

  // =========================================
  // VIDEO PRELOAD
  // The markup ships preload="none" so visitors this popup can't apply to never
  // pay for the file. Once the cart is known to be empty, start pulling it —
  // waiting for the trigger itself means the video buffers in front of a
  // visitor who is already leaving.
  //
  // Driven off the manager's cart event, so a cart that becomes empty later in
  // the session (last item removed) also gets the preload.
  // =========================================
  let preloadStarted = false;

  function maybePreload(count) {
    if (preloadStarted || !video || !gatePasses(count)) return;
    preloadStarted = true;

    try {
      video.preload = 'auto';
      video.load();
    } catch (e) { /* ignore */ }
  }

  document.addEventListener('theme-popups:cart', (e) => {
    maybePreload(e.detail.count);
  });

  // The manager's warm-up fetch may already have landed before this script ran.
  maybePreload(Popups.cartCount());

  // =========================================
  // MODAL SHOW / HIDE
  // =========================================
  let lastFocusedElement = null;
  let showInProgress = false;

  function isOpen() {
    return !popup.hidden;
  }

  // Actually reveal the modal + start playback. No cart gate here — callers
  // are responsible for the empty-cart check.
  // Returns false if another popup holds the screen, in which case nothing is
  // shown and the caller must not treat the popup as fired.
  function openModal() {
    if (!Popups.open(POPUP_ID)) return false;

    lastFocusedElement = document.activeElement;
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');

    // Autoplay muted (always allowed by browsers). User can unmute.
    if (video) {
      video.muted = true;
      popup.dataset.muted = 'true';
      video.currentTime = 0;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () { /* ignore */ });
    }

    // Trap Tab inside the dialog. Without it this popup's aria-modal="true" was
    // a lie: keyboard users could Tab out to the page behind — including onto a
    // checkout button, which the blur's pointer-events:none does not protect.
    //
    // `button[...]` matters: the full-screen overlay div carries the same close
    // attribute and comes first in the DOM, so an unqualified query returned a
    // div and the focus call silently did nothing.
    Popups.trapFocus(popup, popup.querySelector('button[data-exit-intent-video-close]'));

    document.dispatchEvent(new CustomEvent('exit-intent-video:shown'));
    return true;
  }

  // Lock the popup in as "fired" once the empty cart is confirmed. The 24h
  // clock starts here, so a popup that never actually opened (mutex refusal,
  // non-empty cart) does not burn the visitor's one showing for the day.
  function finalize() {
    markShown();
    detachTriggers();
  }

  function showPopup() {
    if (isOpen() || showInProgress || shownWithinDay()) return;

    // Another popup (or a theme drawer/modal) owns the screen → skip this
    // trigger entirely rather than stacking on top of it. Triggers stay armed
    // and nothing is recorded, so the next exit attempt can still fire.
    if (!Popups.canOpen(POPUP_ID)) return;

    const fresh = Popups.isCartFresh();
    const cached = Popups.cartCount();

    // A fresh count that fails the gate means the sibling cart-has-items popup
    // owns this trigger. Nothing to verify — don't spend a /cart.js round trip.
    if (fresh && !gatePasses(cached)) return;

    // Fast path: the count is FRESH and the cart is empty → open immediately,
    // confirm in the background. Freshness is what makes this safe; acting on a
    // stale count is what used to flash this popup over a cart that had items.
    if (fresh) {
      if (!openModal()) return;

      showInProgress = true;
      Popups.refreshCart().then((count) => {
        showInProgress = false;

        // null = the re-check failed, not "cart has items". The count that got
        // us here was seconds old, so trust it and keep the popup up rather
        // than yanking it off the screen over a network hiccup.
        if (count === null || gatePasses(count)) {
          finalize();
        } else {
          hidePopup(); // cart no longer empty → retract, keep armed
        }
      });
      return;
    }

    // Count unknown or stale → verify first so we never flash the popup over a
    // cart that actually has items.
    showInProgress = true;
    Popups.refreshCart().then((count) => {
      showInProgress = false;
      if (gatePasses(count) && openModal()) {
        finalize();
      }
    });
  }

  function hidePopup() {
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
    Popups.close(POPUP_ID);

    if (video) {
      video.pause();
      video.currentTime = 0;
      video.muted = true;
      popup.dataset.muted = 'true';
    }

    Popups.releaseFocus(lastFocusedElement);
  }

  // =========================================
  // DEVICE DETECTION
  // =========================================
  const isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  // =========================================
  // DESKTOP TRIGGER: mouse exits top of viewport
  // =========================================
  function onMouseLeave(e) {
    if (e.clientY <= 0) showPopup();
  }

  // =========================================
  // MOBILE TRIGGER 1: Back button
  // =========================================
  // Deliberately does NOT re-arm the history trap — same race as in
  // assets/exit-intent.js: showPopup() can return before the async cart check
  // opens the dialog, so the manager's "is a popup open?" guard would miss and
  // push a duplicate entry, costing the visitor an extra back press. The manager
  // re-arms on close instead.
  function onPopState() {
    showPopup();
  }

  // =========================================
  // MOBILE TRIGGER 2: App switch / tab hidden
  //
  // The listener stays attached on desktop regardless — returning to the tab is
  // a good moment to refresh the cart count for the fast path.
  // =========================================
  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      // Touch devices only, same call assets/exit-intent.js makes: on desktop
      // this also fires on tab switches, minimising and screen lock, none of
      // which mean leaving. The video would open — and start playing — while
      // nobody is looking at the tab, and burn this visitor's one showing for the
      // day on it. Desktop exits are already covered by mouseleave.
      if (isTouchDevice) showPopup();
    } else {
      // Returned to the tab → refresh the shared count so the next exit
      // attempt can take the fast path. The cart may have changed in another
      // tab, which no cartUpdate event in this one would have reported.
      Popups.refreshCart();
    }
  }

  // =========================================
  // MOBILE TRIGGER 3: Rapid scroll-up burst
  // =========================================
  let lastScrollY = window.scrollY;
  let lastScrollTime = Date.now();

  function onScroll() {
    const now = Date.now();
    const scrollY = window.scrollY;
    const deltaY = lastScrollY - scrollY;
    const deltaTime = now - lastScrollTime;

    if (deltaY > 120 && deltaTime < 250 && scrollY < 300) {
      showPopup();
    }

    lastScrollY = scrollY;
    lastScrollTime = now;
  }

  // =========================================
  // MOBILE TRIGGER 4: Idle timeout
  // =========================================
  let idleTimer = null;
  const IDLE_TIMEOUT_MS = config.mobileIdleSeconds * 1000;

  function resetIdleTimer() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(showPopup, IDLE_TIMEOUT_MS);
  }

  function attachIdleTracker() {
    ['touchstart', 'touchmove', 'click', 'scroll'].forEach((evt) => {
      document.addEventListener(evt, resetIdleTimer, { passive: true });
    });
    resetIdleTimer();
  }

  function detachIdleTracker() {
    clearTimeout(idleTimer);
    ['touchstart', 'touchmove', 'click', 'scroll'].forEach((evt) => {
      document.removeEventListener(evt, resetIdleTimer);
    });
  }

  // =========================================
  // ATTACH / DETACH TRIGGERS (device-branched)
  // Triggers stay armed until the popup fires once. After it fires (empty
  // cart confirmed) they are detached and not re-armed for 24 hours.
  // showPopup() also re-checks the cart each time and no-ops if not empty.
  // =========================================
  function attachTriggers() {
    if (isTouchDevice) {
      Popups.armBackTrap();
      window.addEventListener('popstate', onPopState);
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('scroll', onScroll, { passive: true });
      attachIdleTracker();
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function detachTriggers() {
    document.removeEventListener('mouseleave', onMouseLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('popstate', onPopState);
    detachIdleTracker();
  }

  // Arm unconditionally. The manager has already started warming the cart count
  // and showPopup() re-checks it on every trigger, so there is nothing to wait
  // for here — and nothing to gate on, since the cart can change at any point
  // after this timer runs.
  setTimeout(attachTriggers, ARM_DELAY_MS);

  // =========================================
  // CLOSE HANDLERS
  // =========================================
  popup.querySelectorAll('[data-exit-intent-video-close]').forEach((el) => {
    el.addEventListener('click', hidePopup);
  });

  // The checkout gate (assets/shipping-confirmation-popup.js) outranks this
  // popup and evicts it rather than stacking on top. Step aside when named, so
  // the manager's bookkeeping and what's actually on screen stay in agreement.
  document.addEventListener('theme-popups:preempt', (e) => {
    if (e.detail && e.detail.id === POPUP_ID) hidePopup();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !popup.hidden) hidePopup();
  });

  // =========================================
  // SOUND TOGGLE (unmute / mute)
  // =========================================
  if (soundBtn && video) {
    soundBtn.addEventListener('click', function () {
      video.muted = !video.muted;
      popup.dataset.muted = video.muted ? 'true' : 'false';
      // If unmuting and the video was somehow paused, resume.
      if (!video.muted && video.paused) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () { /* ignore */ });
      }
    });
  }
})();
